import { memo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { Button, Status, Table } from '@wf/components';
import { Tooltip } from '@wf/sw-ui-kit';

import TableWrapper from '../../../../components/TableWrapper';
import TableFooter from '../../../../components/TableFooter';
import { useLicenseContext } from '../../LicenseModeContext';
import { LicenseField } from '../LicenseField';

import { ArrayFields, FeeFields } from '../../types/fieldEnums';
import { feeConfigNames } from '../../types/configNames';

import { invoiceUrlSelector, nationalCurrencySelector, receiptUrlSelector } from '../../../../store/configs/selectors';
import { formatDate } from '../../../../helpers/dateHelpers';
import formatNumber from '../../../../helpers/formatNumber';
import { feesSelector } from '../../store/selectors';
import { COUNTRY } from '../../../../config';

import styles from './Fees.module.scss';

function Fees() {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { fees, amountToBePaid, totalAmount, modify } = useSelector(feesSelector);
  const receiptUrl = useSelector(receiptUrlSelector);
  const invoiceUrl = useSelector(invoiceUrlSelector);

  const nationalCurrency = useSelector(nationalCurrencySelector);
  const { viewMode } = useLicenseContext();

  const receiptNumberCell = (index: number, receiptNumber: string | null) => {
    if (!modify) {
      return receiptNumber;
    } else {
      return (
        <LicenseField.TextField
          name={`${ArrayFields.fees}.${index}.${FeeFields.receiptNumber}`}
          configName={feeConfigNames[FeeFields.receiptNumber]}
          placeholder={t('Write Receipt Number')}
          disabled={!!receiptNumber}
        />
      );
    }
  };

  const receiptDateCell = (index: number, paymentDate: string | null) => {
    if (!modify) {
      return formatDate(paymentDate);
    } else {
      return (
        <LicenseField.DateField
          name={`${ArrayFields.fees}.${index}.${FeeFields.paymentDate}`}
          configName={feeConfigNames[FeeFields.paymentDate]}
          placeholder={t('Write Payment Date')}
          disabled={!!paymentDate}
          maxDate={new Date()}
        />
      );
    }
  };

  return (
    <TableWrapper title={t('List of Fees and Charges')} isTabContent>
      <Table>
        <Table.Grid>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className={styles.firstCell}>{t('#')}</Table.HeaderCell>
              <Table.HeaderCell className={styles.fieldCell}>
                <Tooltip tooltipContent={t('A unique identifier assigned to this invoice')}>
                  {t('Invoice Number')}
                </Tooltip>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Tooltip tooltipContent={t('The date when the invoice was issued')}>{t('Invoice Date')}</Tooltip>
              </Table.HeaderCell>
              <Table.HeaderCell className={styles.fieldCell}>
                <Tooltip tooltipContent={t('The unique number assigned to the receipt for the fee payment')}>
                  {t('Receipt Number')}
                </Tooltip>
              </Table.HeaderCell>
              <Table.HeaderCell className={styles.fieldCell}>
                <Tooltip tooltipContent={t('The date when the fee payment receipt was issued')}>
                  {t('Receipt Date')}
                </Tooltip>
              </Table.HeaderCell>
              <Table.HeaderCell className={styles.statusCell} align="right">
                <Tooltip tooltipContent={t('Status')}>{t('Status')}</Tooltip>
              </Table.HeaderCell>
              <Table.HeaderCell className={styles.actionsCell} align="right">
                <Tooltip tooltipContent={t('Options available for managing this invoice')}>{t('Actions')}</Tooltip>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body maxRows={100}>
            {fees.map(({ receiptNumber, paymentDate, billNumber, invoiceDate, fees }, index: number) => (
              <Table.Expandable key={billNumber} isOpen>
                <Table.Row
                  className={cn({
                    [styles.fieldRow as string]: !receiptNumber && !viewMode,
                  })}
                >
                  <Table.Cell className={styles.firstCell}>{index + 1}</Table.Cell>
                  <Table.Cell className={styles.fieldCell}>{billNumber}</Table.Cell>
                  <Table.Cell>{formatDate(invoiceDate)}</Table.Cell>
                  <Table.Cell className={styles.fieldCell}>{receiptNumberCell(index, receiptNumber)}</Table.Cell>
                  <Table.Cell className={styles.fieldCell}>{receiptDateCell(index, paymentDate)}</Table.Cell>
                  <Table.Cell className={styles.statusCell} align="right">
                    <Status color={receiptNumber ? 'success' : 'typography'}>
                      {t(receiptNumber ? 'Paid' : 'Unpaid')}
                    </Status>
                  </Table.Cell>
                  <Table.Cell align="right" className={styles.actionsCell}>
                    {receiptNumber &&
                      ((COUNTRY === 'niger' && billNumber && invoiceUrl) ||
                        (COUNTRY === 'bangladesh' && receiptUrl)) && (
                        <a
                          href={COUNTRY === 'niger' ? `${invoiceUrl!}${billNumber!}` : `${receiptUrl!}${receiptNumber}`}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <Button
                            title={t('See the receipt associated with this invoice.')}
                            className={styles.viewReceiptButton}
                            ghost
                          >
                            {t('View Receipt')}
                          </Button>
                        </a>
                      )}
                  </Table.Cell>
                </Table.Row>
                <Table.Expandable.Content>
                  <Table inner>
                    <Table.Grid>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell className={styles.firstCell} />
                          <Table.HeaderCell>
                            <Tooltip tooltipContent={t('The unique code assigned to the fee')}>{t('Code')}</Tooltip>
                          </Table.HeaderCell>
                          <Table.HeaderCell>
                            <Tooltip tooltipContent={t('A brief description of the fee')}>{t('Description')}</Tooltip>
                          </Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            <Tooltip
                              tooltipContent={t(
                                'The initial amount before any additional charges or taxes are applied'
                              )}
                            >
                              {t('Base Amount')}
                            </Tooltip>
                          </Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            <Tooltip
                              tooltipContent={t(
                                'The percentage or rate applied to the base amount to calculate the total fee'
                              )}
                            >
                              {t('Rate')}
                            </Tooltip>
                          </Table.HeaderCell>
                          <Table.HeaderCell align="right">
                            <Tooltip
                              tooltipContent={t(
                                'The total amount of the fee, calculated based on the base amount and rate'
                              )}
                            >
                              {t('Amount')}
                            </Tooltip>
                          </Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body maxRows={10}>
                        {fees.map(({ id, feeCode, feeDescription, rate, amount, baseAmount }) => (
                          <Table.Row key={id}>
                            <Table.Cell className={styles.firstCell} />
                            <Table.Cell>{feeCode}</Table.Cell>
                            <Table.Cell>{feeDescription}</Table.Cell>
                            <Table.Cell align="right">
                              {formatNumber({ value: baseAmount, locale: language })}
                            </Table.Cell>
                            <Table.Cell align="right">
                              {formatNumber({
                                value: rate,
                                locale: language,
                                maximumFractionDigits: 7,
                                minimumFractionDigits: 2,
                              })}
                            </Table.Cell>
                            <Table.Cell align="right">{formatNumber({ value: amount, locale: language })}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Grid>
                  </Table>
                </Table.Expandable.Content>
              </Table.Expandable>
            ))}
            <Table.Row className={styles.totalAmountRow}>
              <div>{t('Total Amount in {{currency}}', { currency: nationalCurrency })}</div>
              <span className={styles.totalAmount}>{formatNumber({ value: totalAmount, locale: language })}</span>
            </Table.Row>
            <Table.Row className={cn(styles.totalAmountRow, styles.lastRow)}>
              <div>{t('Total Amount To Be Paid')}</div>
              <span className={styles.totalAmount}>{formatNumber({ value: amountToBePaid, locale: language })}</span>
            </Table.Row>
          </Table.Body>
          <TableFooter page={1} totalCount={fees.length} withPagination={false} />
        </Table.Grid>
      </Table>
    </TableWrapper>
  );
}

export default memo(Fees);
